import React from 'react';
import { IconTrash, IconAdd, IconChannelSelective } from '@/icons';
import { Button } from '@/ui/Button/Button';
import Input from '@/ui/Input/Input';

import { createId } from '@paralleldrive/cuid2';
import { T } from '@/ui/Text/Text';
import { useIntl } from 'react-intl';
import CustomSwitch from '@/ui/Switch/Switch';
import { Flex } from '@/ui/Flex/Flex';
import { GeneralSettingsType } from '../../SettingsSidebar/types';
import { StyledGridContainer } from '../styles';

interface Props {
  screens: GeneralSettingsType['screens'];
  setScreens: (newValue: GeneralSettingsType['screens']) => void;
}

function ScreenHardwareSettings({ screens, setScreens }: Props) {
  const intl = useIntl();

  // --- Utility Functions ---

  const updateScreen = (
    index: number,
    key: 'screenNr' | 'name' | 'touchEnabled',
    value: string | boolean | null,
  ) => {
    const newscreens = [...screens];
    newscreens[index] = {
      ...newscreens[index],
      [key]: value,
    };
    setScreens(newscreens);
  };

  const deleteScreen = (index: number) => {
    const newscreens = screens.filter((_, i) => i !== index);
    setScreens(newscreens);
  };

  const addScreen = () => {
    setScreens([
      ...screens,
      {
        id: createId(),
        screenNr: screens ? screens.length : 0,
        name:
          screens.length >= 1 ? `Screen ${screens.length + 1}` : 'Main Screen',
        touchEnabled: false,
      },
    ]);
  };

  // --- JSX ---

  return (
    <Flex direction="column" gap={16}>
      <T h4>
        {intl.formatMessage({
          defaultMessage: 'Registered Screens',
          id: 'ii86NK',
        })}
      </T>

      {screens.length !== 0 && (
        <StyledGridContainer columns={3}>
          <>
            <T body1>
              {intl.formatMessage({
                defaultMessage: 'Screen Nr.',
                id: 'ZpfaLG',
              })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Name', id: 'HAlOn1' })}
            </T>
            <T body1>
              {intl.formatMessage({ defaultMessage: 'Touch', id: 'lDEhUP' })}
            </T>
            <div />
          </>

          {screens.map((screen, index) => (
            <React.Fragment key={screen.id}>
              <div
                style={{
                  marginTop:
                    (screen.screenNr || screen.screenNr === 0) && screen.name
                      ? 0
                      : '8px',
                }}
              >
                <Input
                  required
                  type="number"
                  size="small"
                  value={screen.screenNr ?? ''}
                  onChange={(e) => updateScreen(index, 'screenNr', String(e))}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Screen',
                    id: 'kzaYlD',
                  })}
                  icon={<IconChannelSelective />}
                />
              </div>

              <div
                style={{
                  marginTop:
                    (screen.screenNr || screen.screenNr === 0) && screen.name
                      ? 0
                      : '8px',
                }}
              >
                <Input
                  required
                  size="small"
                  value={screen.name || ''}
                  onChange={(e) => updateScreen(index, 'name', String(e))}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Name',
                    id: 'HAlOn1',
                  })}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop:
                    (screen.screenNr || screen.screenNr === 0) && screen.name
                      ? 0
                      : '8px',
                }}
              >
                <CustomSwitch
                  checked={screen.touchEnabled}
                  onChange={() =>
                    updateScreen(index, 'touchEnabled', !screen.touchEnabled)
                  }
                />
              </div>
              <Button
                size="minimum"
                mode="transparent"
                onClick={() => deleteScreen(index)}
              >
                <IconTrash />
              </Button>
            </React.Fragment>
          ))}
        </StyledGridContainer>
      )}

      <Button mode="transparent" onClick={addScreen}>
        <IconAdd />
      </Button>
    </Flex>
  );
}

export default ScreenHardwareSettings;
